import { MenuItem, TextField } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers';
import dayjs, { Dayjs } from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import { FieldSchema } from 'api';

// Without this plugin dayjs ignores the format and strict flag, and accepts 02/30/2026.
dayjs.extend(customParseFormat);

// What the reviewer sees and types.
const DISPLAY_FORMAT = 'MM/DD/YYYY';

// What's sent and stored: ISO, the format the model extracts.
const WIRE_FORMAT = 'YYYY-MM-DD';

// Accepted when pre-filling, since older corrections were stored in US format.
const PARSE_FORMATS = [WIRE_FORMAT, DISPLAY_FORMAT];

type CorrectionInputProps = {
  schema: FieldSchema | null;
  value: string | null;
  onChange: (value: string | null) => void;
  /** True while the field is marked Should be blank. */
  disabled?: boolean;
};

/** The correction input for a field's schema: a dropdown for enums, a date
 * picker for dates, free text otherwise. */
export const CorrectionInput = ({
  schema,
  value,
  onChange,
  disabled = false,
}: CorrectionInputProps) => {
  if (schema?.fieldType === 'enum') {
    const labels = schema.labels ?? [];
    // A value that isn't one of the labels can't be pre-selected.
    const selected = value != null && labels.includes(value) ? value : '';
    return (
      <TextField
        select
        fullWidth
        size="small"
        label="Correct value"
        disabled={disabled}
        value={selected}
        onChange={e => onChange(e.target.value || null)}
      >
        {labels.map(label => (
          <MenuItem key={label} value={label}>
            {label}
          </MenuItem>
        ))}
      </TextField>
    );
  }

  if (schema?.fieldType === 'string' && schema.isDate) {
    const parsed = value ? dayjs(value, PARSE_FORMATS, true) : null;
    return (
      <DatePicker
        label="Correct value"
        disabled={disabled}
        format={DISPLAY_FORMAT}
        value={parsed && parsed.isValid() ? parsed : null}
        onChange={(d: Dayjs | null) =>
          onChange(d && d.isValid() ? d.format(WIRE_FORMAT) : null)
        }
        slotProps={{
          field: { clearable: true },
          textField: { size: 'small', fullWidth: true },
        }}
      />
    );
  }

  // Plain strings, fields not in the schema, and unknown types are free text.
  return (
    <TextField
      fullWidth
      multiline
      size="small"
      label="Correct value"
      disabled={disabled}
      value={value ?? ''}
      onChange={e => onChange(e.target.value || null)}
      inputProps={{ maxLength: 500 }}
      helperText={`${(value ?? '').length}/500`}
    />
  );
};
